'use client';

import React, { useId } from 'react';
import { composeTailwindRenderProps } from '@utils';
import {
	TextField as AriaTextField,
	TextFieldProps as AriaTextFieldProps,
	ValidationResult,
} from 'react-aria-components';

import { trimOnBlur } from '@/utils/fieldHelper';
import {
	Description,
	FieldError,
	FieldGroup,
	FieldSuccess,
	FieldVariant,
	FieldWidth,
	iconStyle,
	Input,
	Label,
} from './Field';
import { LoadingSpinner } from './LoadingSpinner';

export interface TextFieldProps extends AriaTextFieldProps {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	placeholder?: string;
	theme?: 'light' | 'dark';
	leftIcon?: React.ReactElement<{ className?: string }>;
	rightIcon?: React.ReactElement<{ className?: string }>;
	isLoading?: boolean;
	variant?: FieldVariant;
	width?: FieldWidth;
}

export function TextField({
	label,
	hint,
	hintChildren,
	errorMessage,
	isValid,
	successMessage,
	theme,
	placeholder,
	leftIcon,
	pattern,
	rightIcon,
	isLoading,
	variant,
	width,
	...props
}: TextFieldProps) {
	const id = useId();

	const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
		trimOnBlur(event, props.onChange, pattern, props.onBlur);
	};

	return (
		<AriaTextField
			id={id}
			{...props}
			onBlur={handleBlur}
			className={composeTailwindRenderProps(props.className, 'flex flex-col gap-2')}
		>
			{({ isInvalid, isDisabled }) => (
				<>
					{label && <Label isRequired={props.isRequired}>{label}</Label>}
					{hint && <Description>{hint}</Description>}
					{hintChildren && hintChildren}
					{errorMessage && <FieldError>{errorMessage}</FieldError>}
					{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
					<FieldGroup
						theme={theme}
						variant={variant}
						width={width}
						isValid={isValid}
						isInvalid={isInvalid}
						isDisabled={isDisabled}
					>
						{leftIcon &&
							React.cloneElement(leftIcon, {
								className: iconStyle({
									theme,
									isInvalid,
									isDisabled,
									isValid,
								}),
							})}
						<Input placeholder={placeholder} theme={theme} />
						{isLoading ? (
							<LoadingSpinner size="small" theme={theme} />
						) : (
							rightIcon &&
							React.cloneElement(rightIcon, {
								className: iconStyle({
									theme,
									isInvalid,
									isDisabled,
									isValid,
								}),
							})
						)}
					</FieldGroup>
				</>
			)}
		</AriaTextField>
	);
}
